import React, { useState } from 'react';
import { 
  X, 
  FileText, 
  CreditCard, 
  Building2, 
  User, 
  Mail, 
  Phone, 
  MapPin,
  Loader2,
  CheckCircle,
  ArrowLeft
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { API_BASE } from '../config/api';

const API = `${API_BASE}/api`;

// PayFast logo
const PayFastLogo = () => (
  <svg viewBox="0 0 100 30" className="h-6">
    <text x="0" y="22" fill="#00457C" fontWeight="bold" fontSize="18">PayFast</text>
  </svg>
);

// PayPal logo
const PayPalLogo = () => (
  <svg viewBox="0 0 100 30" className="h-6">
    <text x="0" y="22" fill="#003087" fontWeight="bold" fontSize="18">PayPal</text>
  </svg>
);

export default function EnrollmentModal({ course, onClose, onSuccess }) {
  const { token, user } = useAuth();
  const [step, setStep] = useState('method'); // 'method', 'invoice', 'payment', 'success'
  const [loading, setLoading] = useState(false);
  const [fundingType, setFundingType] = useState('self'); // 'self' or 'employer'
  
  // Invoice form data
  const [invoiceData, setInvoiceData] = useState({
    // Personal details (pre-filled from user)
    learner_name: user?.full_name || '',
    learner_email: user?.email || '',
    learner_phone: '',
    
    // Self-funded details
    id_number: '',
    billing_address: '',
    
    // Employer details
    company_name: '',
    company_registration: '',
    company_vat: '',
    company_address: '',
    contact_person: '',
    contact_email: '',
    contact_phone: '',
    purchase_order: '',
    
    // Additional notes
    notes: ''
  });

  const handleInputChange = (field, value) => {
    setInvoiceData(prev => ({ ...prev, [field]: value }));
  };

  const handleGenerateInvoice = async () => {
    // Validate required fields
    if (!invoiceData.learner_name || !invoiceData.learner_email) {
      toast.error('Please fill in your name and email');
      return;
    }

    if (fundingType === 'self' && !invoiceData.billing_address) {
      toast.error('Please provide your billing address');
      return;
    }

    if (fundingType === 'employer' && (!invoiceData.company_name || !invoiceData.contact_email)) {
      toast.error('Please provide company name and contact email');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/enrollment/invoice`, {
        course_id: course.course_id,
        funding_type: fundingType,
        ...invoiceData
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Invoice request sent successfully!');
      setStep('success');
    } catch (error) {
      console.error('Invoice error:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate invoice');
    } finally {
      setLoading(false);
    }
  };

  const handlePayFast = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/payment/payfast/initiate`, {
        course_id: course.course_id
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Redirect to PayFast
      if (response.data.redirect_url) {
        window.location.href = response.data.redirect_url;
      }
    } catch (error) {
      console.error('PayFast error:', error);
      toast.error(error.response?.data?.detail || 'Failed to initiate payment');
      setLoading(false);
    }
  };

  const handlePayPal = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/payment/paypal/initiate`, {
        course_id: course.course_id
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Redirect to PayPal
      if (response.data.approval_url) {
        window.location.href = response.data.approval_url;
      }
    } catch (error) {
      console.error('PayPal error:', error);
      toast.error(error.response?.data?.detail || 'Failed to initiate payment');
      setLoading(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-ZA', {
      style: 'currency',
      currency: 'ZAR',
      minimumFractionDigits: 2
    }).format(price);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4">
      <div className="bg-navigate-surface border border-zinc-800 rounded-xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-zinc-800 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-3">
            {step !== 'method' && step !== 'success' && (
              <button 
                onClick={() => setStep('method')}
                className="p-1 text-zinc-400 hover:text-white"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
            )}
            <div>
              <h2 className="font-heading text-xl font-bold text-white">
                {step === 'method' && 'Enroll in Course'}
                {step === 'invoice' && 'Generate Invoice'}
                {step === 'payment' && 'Select Payment Method'}
                {step === 'success' && 'Request Submitted'}
              </h2>
              <p className="text-sm text-zinc-400">{course.title}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 text-zinc-400 hover:text-white">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Step: Method Selection */}
          {step === 'method' && (
            <div className="space-y-4">
              {/* Price Display */}
              <div className="bg-zinc-800/50 rounded-xl p-4 text-center mb-6">
                <p className="text-sm text-zinc-400 mb-1">Course Price</p>
                <p className="text-3xl font-bold text-navigate-gold">
                  {formatPrice(course.price)}
                </p>
              </div>

              <p className="text-zinc-300 mb-4">How would you like to proceed?</p>

              {/* Generate Invoice Option */}
              <button
                onClick={() => setStep('invoice')}
                className="w-full p-4 bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 rounded-xl flex items-center gap-4 transition-colors text-left"
              >
                <div className="w-12 h-12 bg-blue-500/20 rounded-lg flex items-center justify-center">
                  <FileText className="h-6 w-6 text-blue-400" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-white">Generate Invoice</h3>
                  <p className="text-sm text-zinc-400">Request an invoice for self or employer payment</p>
                </div>
              </button>

              {/* Pay Now Option */}
              <button
                onClick={() => setStep('payment')}
                className="w-full p-4 bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 rounded-xl flex items-center gap-4 transition-colors text-left"
              >
                <div className="w-12 h-12 bg-green-500/20 rounded-lg flex items-center justify-center">
                  <CreditCard className="h-6 w-6 text-green-400" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-white">Pay Now</h3>
                  <p className="text-sm text-zinc-400">Pay instantly with PayFast or PayPal</p>
                </div>
              </button>
            </div>
          )}

          {/* Step: Invoice Form */}
          {step === 'invoice' && (
            <div className="space-y-6">
              {/* Funding Type Selection */}
              <div className="space-y-3">
                <Label className="text-white">Who is funding this course?</Label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => setFundingType('self')}
                    className={`p-4 rounded-xl border-2 transition-colors ${
                      fundingType === 'self'
                        ? 'border-navigate-gold bg-navigate-gold/10'
                        : 'border-zinc-700 bg-zinc-800 hover:border-zinc-600'
                    }`}
                  >
                    <User className={`h-6 w-6 mx-auto mb-2 ${fundingType === 'self' ? 'text-navigate-gold' : 'text-zinc-400'}`} />
                    <p className={`font-medium ${fundingType === 'self' ? 'text-white' : 'text-zinc-300'}`}>Self-Funded</p>
                  </button>
                  <button
                    onClick={() => setFundingType('employer')}
                    className={`p-4 rounded-xl border-2 transition-colors ${
                      fundingType === 'employer'
                        ? 'border-navigate-gold bg-navigate-gold/10'
                        : 'border-zinc-700 bg-zinc-800 hover:border-zinc-600'
                    }`}
                  >
                    <Building2 className={`h-6 w-6 mx-auto mb-2 ${fundingType === 'employer' ? 'text-navigate-gold' : 'text-zinc-400'}`} />
                    <p className={`font-medium ${fundingType === 'employer' ? 'text-white' : 'text-zinc-300'}`}>Employer-Funded</p>
                  </button>
                </div>
              </div>

              {/* Learner Details */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                  <User className="h-5 w-5 text-navigate-gold" />
                  Learner Details
                </h3>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="learner_name">Full Name *</Label>
                    <Input
                      id="learner_name"
                      value={invoiceData.learner_name}
                      onChange={(e) => handleInputChange('learner_name', e.target.value)}
                      className="bg-zinc-800 border-zinc-700"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="learner_email">Email *</Label>
                    <Input
                      id="learner_email"
                      type="email"
                      value={invoiceData.learner_email}
                      onChange={(e) => handleInputChange('learner_email', e.target.value)}
                      className="bg-zinc-800 border-zinc-700"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="learner_phone">Phone Number</Label>
                  <Input
                    id="learner_phone"
                    value={invoiceData.learner_phone}
                    onChange={(e) => handleInputChange('learner_phone', e.target.value)}
                    placeholder="+27 XX XXX XXXX"
                    className="bg-zinc-800 border-zinc-700"
                  />
                </div>
              </div>

              {/* Self-Funded Details */}
              {fundingType === 'self' && (
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                    <MapPin className="h-5 w-5 text-navigate-gold" />
                    Billing Details
                  </h3>
                  
                  <div className="space-y-2">
                    <Label htmlFor="id_number">ID/Passport Number</Label>
                    <Input
                      id="id_number"
                      value={invoiceData.id_number}
                      onChange={(e) => handleInputChange('id_number', e.target.value)}
                      className="bg-zinc-800 border-zinc-700"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="billing_address">Billing Address *</Label>
                    <Textarea
                      id="billing_address"
                      value={invoiceData.billing_address}
                      onChange={(e) => handleInputChange('billing_address', e.target.value)}
                      placeholder="Street address, City, Postal Code"
                      className="bg-zinc-800 border-zinc-700"
                      rows={3}
                    />
                  </div>
                </div>
              )}

              {/* Employer-Funded Details */}
              {fundingType === 'employer' && (
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                    <Building2 className="h-5 w-5 text-navigate-gold" />
                    Company Details
                  </h3>
                  
                  <div className="space-y-2">
                    <Label htmlFor="company_name">Company Name *</Label>
                    <Input
                      id="company_name"
                      value={invoiceData.company_name}
                      onChange={(e) => handleInputChange('company_name', e.target.value)}
                      className="bg-zinc-800 border-zinc-700"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="company_registration">Registration Number</Label>
                      <Input
                        id="company_registration"
                        value={invoiceData.company_registration}
                        onChange={(e) => handleInputChange('company_registration', e.target.value)}
                        className="bg-zinc-800 border-zinc-700"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="company_vat">VAT Number</Label>
                      <Input
                        id="company_vat"
                        value={invoiceData.company_vat}
                        onChange={(e) => handleInputChange('company_vat', e.target.value)}
                        className="bg-zinc-800 border-zinc-700"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="company_address">Company Address</Label>
                    <Textarea
                      id="company_address"
                      value={invoiceData.company_address}
                      onChange={(e) => handleInputChange('company_address', e.target.value)}
                      className="bg-zinc-800 border-zinc-700"
                      rows={2}
                    />
                  </div>

                  <h4 className="text-md font-medium text-zinc-300 mt-4">Accounts Contact Person</h4>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="contact_person">Contact Name</Label>
                      <Input
                        id="contact_person"
                        value={invoiceData.contact_person}
                        onChange={(e) => handleInputChange('contact_person', e.target.value)}
                        className="bg-zinc-800 border-zinc-700"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="contact_email">Contact Email *</Label>
                      <Input
                        id="contact_email"
                        type="email"
                        value={invoiceData.contact_email}
                        onChange={(e) => handleInputChange('contact_email', e.target.value)}
                        className="bg-zinc-800 border-zinc-700"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="contact_phone">Contact Phone</Label>
                      <Input
                        id="contact_phone"
                        value={invoiceData.contact_phone}
                        onChange={(e) => handleInputChange('contact_phone', e.target.value)}
                        className="bg-zinc-800 border-zinc-700"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="purchase_order">Purchase Order #</Label>
                      <Input
                        id="purchase_order"
                        value={invoiceData.purchase_order}
                        onChange={(e) => handleInputChange('purchase_order', e.target.value)}
                        className="bg-zinc-800 border-zinc-700"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Additional Notes */}
              <div className="space-y-2">
                <Label htmlFor="notes">Additional Notes</Label>
                <Textarea
                  id="notes"
                  value={invoiceData.notes}
                  onChange={(e) => handleInputChange('notes', e.target.value)}
                  placeholder="Any special requirements or notes..."
                  className="bg-zinc-800 border-zinc-700"
                  rows={2}
                />
              </div>
            </div>
          )}

          {/* Step: Payment Methods */}
          {step === 'payment' && (
            <div className="space-y-4">
              {/* Price Display */}
              <div className="bg-zinc-800/50 rounded-xl p-4 text-center mb-6">
                <p className="text-sm text-zinc-400 mb-1">Amount to Pay</p>
                <p className="text-3xl font-bold text-navigate-gold">
                  {formatPrice(course.price)}
                </p>
              </div>

              <p className="text-zinc-300 mb-4">Select your preferred payment method:</p>

              {/* PayFast (South Africa) */}
              <button
                onClick={handlePayFast}
                disabled={loading}
                className="w-full p-4 bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 rounded-xl flex items-center gap-4 transition-colors disabled:opacity-50"
              >
                <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center">
                  <span className="text-[#00457C] font-bold text-sm">PayFast</span>
                </div>
                <div className="flex-1 text-left">
                  <h3 className="font-semibold text-white">PayFast</h3>
                  <p className="text-sm text-zinc-400">South African payment gateway</p>
                </div>
                {loading && <Loader2 className="h-5 w-5 animate-spin text-zinc-400" />}
              </button>

              {/* PayPal (International) */}
              <button
                onClick={handlePayPal}
                disabled={loading}
                className="w-full p-4 bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 rounded-xl flex items-center gap-4 transition-colors disabled:opacity-50"
              >
                <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center">
                  <span className="text-[#003087] font-bold text-sm">PayPal</span>
                </div>
                <div className="flex-1 text-left">
                  <h3 className="font-semibold text-white">PayPal</h3>
                  <p className="text-sm text-zinc-400">International payments</p>
                </div>
                {loading && <Loader2 className="h-5 w-5 animate-spin text-zinc-400" />}
              </button>

              <p className="text-xs text-zinc-500 text-center mt-4">
                Secure payment processing. Your card details are never stored on our servers.
              </p>
            </div>
          )}

          {/* Step: Success */}
          {step === 'success' && (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="h-8 w-8 text-green-400" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Invoice Request Submitted!</h3>
              <p className="text-zinc-400 mb-6">
                We've sent your invoice request to our team. You'll receive an invoice at{' '}
                <span className="text-white">{invoiceData.learner_email}</span> shortly.
              </p>
              <p className="text-sm text-zinc-500">
                If you have any questions, contact us at{' '}
                <a href="mailto:hello@navigatelearning.io" className="text-navigate-gold hover:underline">
                  hello@navigatelearning.io
                </a>
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        {step === 'invoice' && (
          <div className="px-6 py-4 border-t border-zinc-800 shrink-0">
            <button
              onClick={handleGenerateInvoice}
              disabled={loading}
              className="w-full btn-gold flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {loading ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <FileText className="h-5 w-5" />
                  Submit Invoice Request
                </>
              )}
            </button>
          </div>
        )}

        {step === 'success' && (
          <div className="px-6 py-4 border-t border-zinc-800 shrink-0">
            <button
              onClick={onClose}
              className="w-full btn-gold"
            >
              Close
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
